import { observer } from 'mobx-react-lite'
import React from 'react'

import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import Stack from '@mui/material/Stack'

import { useTheme } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

import { CurrencyText, useGlobalCopyStore } from '../../common/'
import { fromTimestampToMonthYear } from '../../core/utils/DateUtil'

import { GainsLosses } from '../../core/types/NetWorth'
import NetWorthChange from './NetWorthChange'

const GainsLossesDetail: React.FC<GainsLosses> = ({
  netWorth,
  gains,
  losses,
  totalGains,
  totalLosses,
}) => {
  const { net_worth: copy } = useGlobalCopyStore()

  const theme = useTheme()

  return (
    <>
      <Stack alignItems="center" justifyContent="space-between" mb={8} pt={24}>
        <Text variant="body2">{fromTimestampToMonthYear(netWorth?.x)}</Text>
        <Text variant="body1">{copy.total_change}</Text>
        {netWorth && (
          <NetWorthChange currencyVariant="h1" iconSize={32} netWorth={netWorth} theme={theme} />
        )}
      </Stack>
      <List>
        <ListSubheader sx={{ mb: 8, mt: 12, mx: 8 }}>
          <ListItemText
            primary={
              <Stack direction="row" justifyContent="space-between">
                <Text sx={{ fontWeight: 700 }} variant="caption">
                  {copy.gains}
                </Text>
                <CurrencyText amount={totalGains} sx={{ fontWeight: 400 }} variant="caption" />
              </Stack>
            }
          />
        </ListSubheader>
        {gains?.map((gain) => (
          <React.Fragment key={gain.accountName}>
            <ListItem sx={{ backgroundColor: theme.palette.background.paper, py: 14 }}>
              <ListItemText
                primary={
                  <Stack direction="row" justifyContent="space-between">
                    <Text sx={{ mr: 10 }} variant="body1">
                      {gain.accountName}
                    </Text>
                    <CurrencyText
                      amount={gain.gain}
                      sx={{ color: theme.palette.success.main, flexShrink: 0, fontWeight: 700 }}
                      variant="body1"
                    />
                  </Stack>
                }
                sx={{ px: 16 }}
              />
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}

        <ListSubheader sx={{ mb: 8, mt: 12, mx: 8 }}>
          <ListItemText
            primary={
              <Stack direction="row" justifyContent="space-between">
                <Text sx={{ fontWeight: 700 }} variant="caption">
                  {copy.losses}
                </Text>
                <CurrencyText amount={totalLosses} sx={{ fontWeight: 400 }} variant="caption" />
              </Stack>
            }
          />
        </ListSubheader>
        {losses?.map((loss) => (
          <React.Fragment key={loss.accountName}>
            <ListItem sx={{ backgroundColor: theme.palette.background.paper, py: 14 }}>
              <ListItemText
                primary={
                  <Stack direction="row" justifyContent="space-between">
                    <Text
                      sx={{
                        mr: 10,
                      }}
                      variant="body1"
                    >
                      {loss.accountName}
                    </Text>
                    <CurrencyText
                      amount={loss.loss}
                      sx={{ flexShrink: 0, fontWeight: 700 }}
                      variant="body1"
                    />
                  </Stack>
                }
                sx={{ px: 16 }}
              />
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>
    </>
  )
}

export default observer(GainsLossesDetail)
